/**
 * Password Utility Functions
 *
 * Helper functions for password strength calculation and validation
 * Used in authentication flows
 */

/**
 * Password strength information
 */
export interface PasswordStrengthInfo {
  text: string
  color: string
  textColor: string
  width: string
}

/**
 * Calculate password strength on a 0-4 scale
 *
 * Scoring criteria:
 * - Length (8+ chars, 12+ chars)
 * - Character variety (lowercase, uppercase, Hebrew, numbers, special chars)
 *
 * @param password - Password string to evaluate
 * @returns Strength score from 0 (very weak) to 4 (strong)
 *
 * @example
 * calculatePasswordStrength('abc')     // 1 (very weak)
 * calculatePasswordStrength('Abc123!')  // 4 (strong)
 * calculatePasswordStrength('הסיסמה456#') // 4 (strong with Hebrew)
 */
export function calculatePasswordStrength(password: string): number {
  let strength = 0

  // Length checks
  if (password.length >= 8) strength++
  if (password.length >= 12) strength++

  // Character variety checks
  if (/[a-z]/.test(password)) strength++ // lowercase English
  if (/[A-Z]/.test(password)) strength++ // uppercase English
  if (/[\u0590-\u05FF]/.test(password)) strength++ // Hebrew characters
  if (/[0-9]/.test(password)) strength++ // numbers
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password)) strength++ // special chars

  // Cap at 4 for display purposes
  return Math.min(strength, 4)
}

/**
 * Get visual representation of password strength
 *
 * Returns color, text, and width information for UI display
 *
 * @param strength - Password strength score (0-4)
 * @returns Object with Hebrew text label, colors, and progress width
 *
 * @example
 * const info = getPasswordStrengthInfo(3)
 * // { text: 'בינונית', color: 'bg-yellow-500', textColor: 'text-yellow-600', width: '75%' }
 */
export function getPasswordStrengthInfo(strength: number): PasswordStrengthInfo {
  switch (strength) {
    case 0:
    case 1:
      return {
        text: 'חלשה מאוד',
        color: 'bg-red-500',
        textColor: 'text-red-600',
        width: '25%'
      }
    case 2:
      return {
        text: 'חלשה',
        color: 'bg-orange-500',
        textColor: 'text-orange-600',
        width: '50%'
      }
    case 3:
      return {
        text: 'בינונית',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        width: '75%'
      }
    case 4:
      return {
        text: 'חזקה',
        color: 'bg-green-500',
        textColor: 'text-green-600',
        width: '100%'
      }
    default:
      return {
        text: '',
        color: 'bg-gray-200',
        textColor: 'text-gray-400',
        width: '0%'
      }
  }
}

/**
 * Validate if two passwords match
 *
 * @param password - Primary password
 * @param confirmPassword - Confirmation password
 * @returns true if passwords match, false if they don't, null if confirm is empty
 *
 * @example
 * checkPasswordsMatch('test123', 'test123')  // true
 * checkPasswordsMatch('test123', 'different') // false
 * checkPasswordsMatch('test123', '')          // null
 */
export function checkPasswordsMatch(
  password: string,
  confirmPassword: string
): boolean | null {
  if (!confirmPassword) {
    return null // Not checked yet
  }
  return password === confirmPassword
}

/**
 * Validate password meets minimum requirements
 *
 * @param password - Password to validate
 * @returns Object with isValid flag and error message in Hebrew
 *
 * @example
 * validatePassword('abc')
 * // { isValid: false, error: 'הסיסמה חייבת להכיל לפחות 6 תווים' }
 */
export function validatePassword(password: string): {
  isValid: boolean
  error?: string
} {
  if (password.length < 6) {
    return {
      isValid: false,
      error: 'הסיסמה חייבת להכיל לפחות 6 תווים'
    }
  }

  const strength = calculatePasswordStrength(password)
  if (strength < 2) {
    return {
      isValid: false,
      error: 'מומלץ להשתמש בסיסמה חזקה יותר (8+ תווים, אותיות גדולות/קטנות, מספרים)'
    }
  }

  return { isValid: true }
}
